import React from 'react';
import type { SimilarCompound } from '../../../../types/targetUniverse';
import StructureImage from '../../StructureImage';

// Reference colour palette — DO NOT CHANGE
const C = {
  textMuted: '#64748b',
  textDim: '#475569',
  projectTarget: '#22d3ee',
  warning: '#f59e0b',
  danger: '#ef4444',
  similarity: '#ec4899',
};

interface Props {
  similarCompounds: SimilarCompound[];
  projectTarget?: string;
}

const SimilarityCollision: React.FC<Props> = ({ similarCompounds, projectTarget }) => {
  if (similarCompounds.length === 0) {
    return (
      <div style={{
        color: C.textDim, fontSize: 12,
        fontFamily: "'JetBrains Mono', monospace",
        textAlign: 'center', padding: '40px 20px',
      }}>
        No similar compounds found (Tanimoto &ge; 0.7)
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, padding: '0 4px' }}>
      <div style={{ color: C.textMuted, fontSize: 11, marginBottom: 12 }}>
        Compounds &ge;80% Tanimoto similarity hitting other targets
      </div>
      {similarCompounds.map((comp, i) => (
        <div key={i} style={{
          padding: '8px 10px', marginBottom: 4,
          background: 'rgba(236,72,153,0.06)',
          border: '1px solid rgba(236,72,153,0.2)',
          borderRadius: 6,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ color: C.similarity, fontWeight: 600 }}>
              {(comp.tanimoto * 100).toFixed(0)}% similar
            </span>
          </div>
          <div style={{ marginBottom: 4 }}>
            <StructureImage smiles={comp.smiles} size={80} />
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {comp.unique_targets.map(t => {
              const isProject = projectTarget && t.toLowerCase() === projectTarget.toLowerCase();
              return (
                <span key={t} style={{
                  padding: '2px 6px', borderRadius: 3, fontSize: 9,
                  background: isProject ? 'rgba(34,211,238,0.15)' : 'rgba(245,158,11,0.15)',
                  color: isProject ? C.projectTarget : C.warning,
                  border: `1px solid ${isProject ? 'rgba(34,211,238,0.3)' : 'rgba(245,158,11,0.3)'}`,
                }}>
                  {t}
                </span>
              );
            })}
          </div>
        </div>
      ))}
      <div style={{ color: C.textDim, fontSize: 10, marginTop: 12 }}>
        {similarCompounds.length} similar compound{similarCompounds.length !== 1 ? 's' : ''} found
      </div>
    </div>
  );
};

export default SimilarityCollision;
